"use client";

import { HintText } from "@admin-ui/components/base/input/hint-text";
import { Label } from "@admin-ui/components/base/input/label";
import { cx } from "@admin-ui/utils/cx";
import { Minus, Plus } from "@untitledui/icons";
import type { ReactNode } from "react";
import {
  Button as AriaButton,
  Group as AriaGroup,
  Input as AriaInput,
  NumberField as AriaNumberField,
  type NumberFieldProps as AriaNumberFieldProps,
} from "react-aria-components";

interface InputNumberProps extends Omit<AriaNumberFieldProps, "children"> {
  label?: string;
  hint?: ReactNode;
  size?: "sm" | "md" | "lg";
  placeholder?: string;
  className?: string;
}

const sizes = {
  sm: { input: "py-2 px-3 text-sm", button: "h-9 w-9" },
  md: { input: "py-2 px-3 text-md", button: "h-10 w-10" },
  lg: { input: "py-2.5 px-3.5 text-md", button: "h-11 w-11" },
};

export function InputNumber({ label, hint, size = "md", placeholder, className, ...props }: InputNumberProps) {
  return (
    <AriaNumberField {...props} className={cx("flex flex-col gap-1.5", className)}>
      {label && <Label>{label}</Label>}
      <AriaGroup
        className={cx(
          "flex items-stretch overflow-hidden rounded-lg bg-primary shadow-xs ring-1 ring-primary outline-hidden ring-inset",
          "focus-within:ring-2 focus-within:ring-brand",
          props.isDisabled && "opacity-50",
          props.isInvalid && "ring-error",
        )}
      >
        <AriaButton
          slot="decrement"
          className={cx(
            "flex items-center justify-center border-r border-secondary text-secondary outline-hidden hover:bg-secondary disabled:opacity-40",
            sizes[size].button,
          )}
        >
          <Minus className="size-4" aria-hidden />
        </AriaButton>
        <AriaInput
          placeholder={placeholder}
          className={cx(
            "flex-1 bg-transparent text-center font-medium text-primary outline-hidden placeholder:text-fg-quaternary",
            sizes[size].input,
          )}
        />
        <AriaButton
          slot="increment"
          className={cx(
            "flex items-center justify-center border-l border-secondary text-secondary outline-hidden hover:bg-secondary disabled:opacity-40",
            sizes[size].button,
          )}
        >
          <Plus className="size-4" aria-hidden />
        </AriaButton>
      </AriaGroup>
      {hint && <HintText>{hint}</HintText>}
    </AriaNumberField>
  );
}
